import React from 'react'
import styled from 'styled-components'
import Grid, {GridSize} from '@material-ui/core/Grid'
import {Draggable} from 'react-beautiful-dnd'
import {useSimpleBlogTemplate} from 'Event/template/SimpleBlog'
import {useEditMode} from 'Event/EditModeProvider'
import SponsorForm from 'Event/Sponsors/SponsorForm'
import Body from 'Event/template/SimpleBlog/Sponsors/SponsorList/Card/Body'
import Buttons from 'Event/Sponsors/Buttons'
import {useToggleArray} from 'lib/toggle'
import Divider from 'Event/Sponsors/Divider'
import Logo from 'Event/Sponsors/Logo'
import {Sponsor} from 'Event/Sponsors'
import SponsorConfig, {SponsorConfigurable} from 'Event/Sponsors/SponsorConfig'
import {useAttendeeVariables} from 'Event'
import TextContent from 'lib/ui/form/HTMLEditor/HTMLContent'

type SponsorProps = {
  index: number
  id: string
  sponsor: Sponsor
  className?: string
  isLast: boolean
}

export default function Card(props: SponsorProps) {
  const {index, id} = props

  const isEditMode = useEditMode()
  if (!isEditMode) {
    return <Content {...props} />
  }

  return (
    <Draggable draggableId={id} index={index}>
      {(provided) => (
        <div
          ref={provided.innerRef}
          className={props.className}
          {...provided.draggableProps}
          {...provided.dragHandleProps}
        >
          <Content {...props} />
        </div>
      )}
    </Draggable>
  )
}

function Content(props: SponsorProps) {
  const {sponsor} = props
  const template = useSimpleBlogTemplate()
  const [showingForm, toggleForm] = useToggleArray()
  const v = useAttendeeVariables()

  return (
    <>
      <Box aria-label="sponsor" className={props.className}>
        <SponsorConfigurable id={props.id}>
          <SponsorConfig id={props.id} sponsor={sponsor} />
          <>
            <DesktopContainer>
              <Top>
                <SponsorForm
                  sponsor={sponsor}
                  showing={showingForm}
                  onClose={toggleForm}
                />
                <Left
                  sponsor={sponsor}
                  id={props.id}
                  size={template.sponsors.imageSize}
                />
                <StyledBody
                  sponsor={sponsor}
                  toggleForm={toggleForm}
                  textColor={template.textColor}
                  id={props.id}
                />
              </Top>
              <BottomButtons sponsor={sponsor} id={props.id} />
            </DesktopContainer>
            <MobileContainer>
              <SponsorForm
                sponsor={sponsor}
                showing={showingForm}
                onClose={toggleForm}
              />
              <Body
                sponsor={sponsor}
                toggleForm={toggleForm}
                textColor={template.textColor}
                id={props.id}
              />
              <Left
                sponsor={sponsor}
                id={props.id}
                size={template.sponsors.imageSize}
              />
              <TextContent>{v(sponsor.description)}</TextContent>
              <BottomButtons sponsor={sponsor} id={props.id} />
            </MobileContainer>
          </>
        </SponsorConfigurable>
      </Box>
      <Divider
        isLast={props.isLast}
        showingSeparator={template.sponsors.hasDivider}
        spacing={template.sponsors.spacing}
      />
    </>
  )
}

function Left(props: {sponsor: Sponsor; id: string; size: GridSize}) {
  const {id, sponsor, size} = props
  const {image: logo} = sponsor

  if (!logo) {
    return null
  }

  return (
    <LeftContainer item xs={size}>
      <Logo sponsor={sponsor} />
      <Buttons sponsorId={id} />
    </LeftContainer>
  )
}

function BottomButtons(props: {sponsor: Sponsor; id: string}) {
  const logo = props.sponsor.image

  if (!logo) {
    return <Buttons sponsorId={props.id} />
  }

  return null
}

const DesktopContainer = styled.div`
  display: none;

  @media (min-width: ${(props) => props.theme.breakpoints.sm}) {
    display: initial;
  }
`

const MobileContainer = styled.div`
  display: initial;

  @media (min-width: ${(props) => props.theme.breakpoints.sm}) {
    display: none;
  }
`

const Box = styled.div`
  position: relative;
  outline: none;
`

const LeftContainer = styled(Grid)`
  width: 100%;
  max-width: 100%;

  @media (min-width: ${(props) => props.theme.breakpoints.sm}) {
    margin: ${(props) =>
      `${props.theme.spacing[5]} ${props.theme.spacing[4]} ${props.theme.spacing[4]} 0`};
    max-width: initial;
  }
`

const StyledBody = styled(Body)`
  margin-left: ${(props) => props.theme.spacing[5]};
`

const Top = styled.div`
  display: flex;
`
